import { dispatchAction } from '@ardoq/rxbeach';
import { ViewIds } from '@ardoq/api-types';
import { updateViewSettings } from '@ardoq/view-settings';
import { isEqual } from 'lodash';
import {
  type BlocksArrangement,
  parseTitle,
} from 'tabview/assistantBlocks/arrangementRecipe';
import { defineAiControlledCommand } from 'aiControlledCommands/defineAiControlledCommand';
import type { AiControlledCommand } from 'aiControlledCommands/types';
import { ASSISTANT_BLOCKS_DESCRIPTION } from './descriptions';
import { type BlocksContextArgs, buildBlocksContext } from './context';
import {
  type BlocksCommandContext,
  isBlocksCommandContext,
  LAYOUT_COMMAND_METADATA,
  type ResolvedBlocksLayout,
  validateLayout,
} from './layoutCommand';

/** Saves the arrangements and the title shown above the grid. */
export const executeAssistantBlocksLayout = ({
  recipe,
  expectedArrangements,
}: Pick<ResolvedBlocksLayout, 'recipe' | 'expectedArrangements'>): void =>
  dispatchAction(
    updateViewSettings({
      viewId: ViewIds.ASSISTANT_BLOCKS,
      settings: { arrangements: expectedArrangements, title: recipe.title },
      persistent: true,
    })
  );

export const assistantBlocksLayoutCommandDefinition = defineAiControlledCommand<
  ResolvedBlocksLayout,
  BlocksCommandContext
>({
  ...LAYOUT_COMMAND_METADATA,
  description: ASSISTANT_BLOCKS_DESCRIPTION,
  isContext: isBlocksCommandContext,
  validate: (rawPayload, _context, state) =>
    validateLayout({
      viewId: ViewIds.ASSISTANT_BLOCKS,
      rawPayload,
      state,
      findGroupLinks: () => [],
    }),
  execute: executeAssistantBlocksLayout,
  describeApplied: ({ report }) => report,
  isApplied: ({ recipe, expectedArrangements }, context) =>
    context.title === recipe.title &&
    isEqual(context.arrangements, expectedArrangements),
});

type BuildAssistantBlocksLayoutCommandArgs = Omit<
  BlocksContextArgs,
  'storedLayout'
> & {
  title: unknown;
  arrangements: BlocksArrangement[];
};

export const buildAssistantBlocksLayoutCommand = ({
  title,
  arrangements,
  ...contextArgs
}: BuildAssistantBlocksLayoutCommandArgs): AiControlledCommand<
  typeof assistantBlocksLayoutCommandDefinition
> => ({
  ...assistantBlocksLayoutCommandDefinition,
  context: {
    ...buildBlocksContext({ ...contextArgs, arrangements, storedLayout: null }),
    title: parseTitle(title),
    arrangements,
  },
});
